import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getErrorMessage } from '@/lib/api'
import type { AttendanceRequestType } from '@/services/workers'

function dateTimeWithOffset(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const offset = -parsed.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0')
  const withSeconds = value.length === 16 ? `${value}:00` : value
  return `${withSeconds}${sign}${hours}:${minutes}`
}

export function AttendanceRequestDialog({
  open,
  onOpenChange,
  employeeName,
  workerId,
  workDate,
  currentCheckIn,
  currentCheckOut,
  isPending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeName: string
  workerId: string
  workDate: string
  currentCheckIn?: string
  currentCheckOut?: string
  isPending?: boolean
  onSubmit: (payload: {
    workerId: string
    workDate: string
    requestType: AttendanceRequestType
    requestedCheckIn?: string
    requestedCheckOut?: string
    reason?: string
  }) => Promise<unknown> | void
}) {
  const [requestType, setRequestType] = useState<AttendanceRequestType>(currentCheckOut ? 'time_correction' : 'missing_punch')
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) return
    setRequestType(currentCheckOut ? 'time_correction' : 'missing_punch')
    setCheckIn('')
    setCheckOut('')
    setReason('')
  }, [currentCheckOut, open])

  const submit = async () => {
    if (!workerId.trim()) {
      toast.error('Employee is required')
      return
    }
    if (!workDate.trim()) {
      toast.error('Date is required')
      return
    }
    if (!checkIn && !checkOut) {
      toast.error('Enter a check-in time, a check-out time, or both')
      return
    }
    try {
      await onSubmit({
        workerId,
        workDate,
        requestType,
        requestedCheckIn: checkIn ? dateTimeWithOffset(`${workDate}T${checkIn}`) : undefined,
        requestedCheckOut: checkOut ? dateTimeWithOffset(`${workDate}T${checkOut}`) : undefined,
        reason: reason.trim() || undefined,
      })
      onOpenChange(false)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Raise request</DialogTitle>
          <DialogDescription>Ask for a missing punch or a time correction on this time log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Employee</Label>
            <Input value={employeeName} disabled />
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={workDate} disabled />
          </div>
          <div className="space-y-2">
            <Label>Request</Label>
            <Select value={requestType} onValueChange={(value) => setRequestType(value as AttendanceRequestType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="missing_punch">Missing punch</SelectItem>
                <SelectItem value="time_correction">Time correction</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Requested check-in</Label>
              <Input type="time" value={checkIn} onChange={(event) => setCheckIn(event.target.value)} />
              {currentCheckIn ? <p className="text-xs text-slate-500">Recorded {currentCheckIn}</p> : null}
            </div>
            <div className="space-y-2">
              <Label>Requested check-out</Label>
              <Input type="time" value={checkOut} onChange={(event) => setCheckOut(event.target.value)} />
              {currentCheckOut ? <p className="text-xs text-slate-500">Recorded {currentCheckOut}</p> : null}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why this punch needs a change" />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button type="button" onClick={() => void submit()} disabled={isPending}>{isPending ? 'Saving...' : 'Raise request'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
